import React, { createContext, useContext, useEffect, useState } from 'react'

type ThemeMode = 'light' | 'dark' | 'auto'

interface ThemeContextType {
  isDark: boolean
  mode: ThemeMode
  cycleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType>({ isDark: false, mode: 'light', cycleTheme: () => {} })

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    return (localStorage.getItem('theme') as ThemeMode) || 'light'
  })

  const getSystemDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches

  const isDark = mode === 'dark' || (mode === 'auto' && getSystemDark())

  useEffect(() => {
    const apply = (dark: boolean) => {
      document.documentElement.classList.add('dark-transition')
      if (dark) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
      setTimeout(() => document.documentElement.classList.remove('dark-transition'), 400)
    }

    if (mode === 'auto') {
      const isNightTime = () => {
        const h = new Date().getHours()
        return h >= 18 || h < 6
      }
      apply(isNightTime())
      // Check every minute for time-based switching
      const interval = setInterval(() => apply(isNightTime()), 60000)
      return () => clearInterval(interval)
    } else {
      apply(mode === 'dark')
    }
  }, [mode])

  const cycleTheme = () => {
    setMode(prev => {
      const next: ThemeMode = prev === 'light' ? 'dark' : prev === 'dark' ? 'auto' : 'light'
      localStorage.setItem('theme', next)
      return next
    })
  }

  return (
    <ThemeContext.Provider value={{ isDark, mode, cycleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
